import { CartItemList } from '@/feature/cart/components/cart-item-list/CartItemList'
import { twMerge } from '@/lib/classname/twMerge'
import { Accordion } from '@/ui/accordion/Accordion'
import { AccordionItem } from '@/ui/accordion/AccordionItem'
import { AccordionItemHeader } from '@/ui/accordion/_components/AccordionItemHeader'
import { AccordionItemPanel } from '@/ui/accordion/_components/AccordionItemPanel'
import type { IllustrationName } from '@/ui/illustrations/illustration/Illustration'
import { Container } from '@/ui/layouts/container/Container'
import { Divider } from '@/ui/layouts/divider/Divider'
import { Link } from '@/ui/links/link/Link.client'
import { Skin } from '@/ui/skin/Skin'
import { gql } from '@gql-generated/gql'
import type { Footer_ReassuranceCardsFragment } from '@gql-generated/graphql'
import type { FooterLinksSectionProps } from './Footer.schema'
import { FooterSchema } from './Footer.schema'
import { FooterAppDownload } from './FooterAppDownload.client'
import { FooterFindUsButton } from './FooterFindUsButton.client'
import { FooterLink, type FooterLinkSection } from './FooterLink'
import { FooterMap } from './FooterMap.client'
import { FooterSocial } from './FooterSocial.client'
import footerJSON from './footer.json'
import { ReassuranceCard } from './reassurance-card/ReassuranceCard'
import { sortFooterLinks } from './services/footer.services'

gql(/* GraphQL */ `
  fragment Footer_ReassuranceCards on CMS_ReassuranceCardBlock {
    id
    blockType
    blockName
    illustration
    line1
    line2
    href
  }
`)

function FooterLinksSection({
  title,
  links,
  linkSection,
  className,
}: FooterLinksSectionProps & {
  linkSection: FooterLinkSection
}): React.ReactElement {
  return (
    <div className={twMerge(className)}>
      <p className="mb-2 box-border block break-inside-avoid items-center font-bold">{title}</p>
      {Object.entries(links).map(([key, { name, ...props }]) => (
        <FooterLink key={key} name={name} linkSection={linkSection} {...props} />
      ))}
    </div>
  )
}

type FooterProps = {
  isFaqEnabled?: boolean
  isStoreLocatorEnabled?: boolean
  reassuranceCards: Footer_ReassuranceCardsFragment[] | null | undefined
}

function isLinkToBeDisplayed(
  key: string,
  config: { isFaqEnabled?: boolean; isStoreLocatorEnabled?: boolean },
): boolean {
  if (key === 'faq' && !config.isFaqEnabled) {
    return false
  }
  // eslint-disable-next-line @stylistic/ts/padding-line-between-statements, sonarjs/prefer-single-boolean-return
  if (key === 'stores' && !config.isStoreLocatorEnabled) {
    return false
  }

  return true
}

export function Footer({
  isFaqEnabled,
  isStoreLocatorEnabled,
  reassuranceCards,
}: FooterProps): React.ReactElement {
  const {
    reassurance: reassuranceCardsJSON,
    findUs,
    appDownload,
    links,
    legals,
    social,
  } = FooterSchema.parse(footerJSON).footer

  const reassuranceCardsData = reassuranceCards?.length
    ? reassuranceCards
    : Object.values(reassuranceCardsJSON)

  const availableLinkSections = Object.entries(links).reduce<[string, FooterLinksSectionProps][]>(
    (acc, [key, section]) => {
      if (section && Object.keys(section.links).length > 0) {
        // Filter out specific links in the needHelp section based on feature flags
        if (key === 'needHelp') {
          const filteredLinks = Object.fromEntries(
            Object.entries(section.links).filter(([linkKey]) =>
              isLinkToBeDisplayed(linkKey, { isFaqEnabled, isStoreLocatorEnabled }),
            ),
          )

          // Add the section only if it has links after filtering
          if (Object.keys(filteredLinks).length > 0) {
            acc.push([key, { ...section, links: filteredLinks }])
          }
        } else {
          acc.push([key, section])
        }
      }

      return acc
    },
    [],
  )

  const desktopLinkSections = availableLinkSections.filter(([key]) => key !== 'products')
  const productLinksSection = availableLinkSections.find(([key]) => key === 'products')
  const hasDesktopLinks = desktopLinkSections.length > 0 || !!productLinksSection

  return (
    <footer
      tabIndex={-1} // https://www.magentaa11y.com/checklist-web/footer/
      id="app-footer"
      className="print:hidden"
    >
      {!!reassuranceCardsData?.length && (
        <Skin tint="brand-1-alt" className="max-w-full rounded-tl-md rounded-tr-md">
          <Container>
            {/* Reassurance */}
            <div className="box-border flex w-full flex-wrap justify-center gap-2 py-4 tablet:px-2 desktop:px-4 desktop:py-5 desktop-lg:px-10">
              {reassuranceCardsData?.map((card) => (
                <ReassuranceCard
                  key={`${card.line1}-${card.line2}`}
                  {...card}
                  illustration={card.illustration as IllustrationName}
                />
              ))}
            </div>
          </Container>
        </Skin>
      )}

      <Skin tint="brand-2">
        <Container>
          <div className="p-4">
            <div className="flex flex-col justify-center gap-2 desktop:mb-6 desktop:flex-row desktop:gap-8">
              {/* Find Us */}
              <div className="flex w-full flex-col items-center text-center desktop:w-[18.75rem]">
                {/* Map */}
                <FooterMap />
                <div className="mt-4">
                  <p className="mb-0.5 font-bold">{findUs.title}</p>
                  <p className={twMerge(findUs.name && 'mb-2')}>{findUs.caption}</p>
                  {findUs.name && <FooterFindUsButton name={findUs.name} />}
                </div>
              </div>
              {/* Links - Desktop */}
              <div className="hidden flex-col desktop:flex desktop:gap-4">
                {hasDesktopLinks && (
                  <div className="flex flex-row gap-9">
                    {desktopLinkSections.length > 0 && (
                      <div className="flex flex-col desktop:gap-4">
                        {desktopLinkSections.map(([linksSectionKey, { links, title }], index) => (
                          <FooterLinksSection
                            key={linksSectionKey}
                            className={index > 0 ? 'mt-auto' : undefined}
                            title={title}
                            links={links}
                            linkSection={linksSectionKey as FooterLinkSection}
                          />
                        ))}
                      </div>
                    )}
                    {productLinksSection && (
                      <div>
                        <FooterLinksSection {...productLinksSection[1]} linkSection="products" />
                      </div>
                    )}
                  </div>
                )}
                {(social || appDownload) && (
                  <div className="flex flex-row desktop:gap-x-9">
                    {social && <FooterSocial social={social} />}
                    {appDownload && <FooterAppDownload appDownload={appDownload} />}
                  </div>
                )}
              </div>
              {/* Links - Mobile */}
              <Accordion className="mt-6 block desktop:mt-0 desktop:hidden">
                {Object.entries(links)
                  .sort(sortFooterLinks)
                  .map(([linksKey, { title, links }]) => (
                    <AccordionItem key={linksKey} id={title}>
                      <AccordionItemHeader title={title} />
                      <AccordionItemPanel>
                        <CartItemList>
                          {Object.entries(links).map(([linkKey, linkProps]) => (
                            <CartItemList.Item key={linkKey}>
                              <FooterLink
                                {...linkProps}
                                key={linkKey}
                                linkSection={linksKey as FooterLinkSection}
                              />
                            </CartItemList.Item>
                          ))}
                        </CartItemList>
                      </AccordionItemPanel>
                    </AccordionItem>
                  ))}
              </Accordion>
            </div>

            <Divider direction="horizontal" variant="line" className="mt-1 desktop:mt-0" />

            {(social || appDownload) && (
              <div className="flex flex-col gap-3 py-3 desktop:hidden">
                {social && <FooterSocial social={social} />}
                {appDownload && <FooterAppDownload appDownload={appDownload} />}
                <Divider direction="horizontal" variant="line" className="mt-1" />
              </div>
            )}

            {/* Legals */}
            <div className="mt-3 flex flex-wrap justify-center gap-3 px-3 desktop:px-0">
              {Object.entries(legals.links).map(([key, { name, href }]) => (
                <Link
                  variant="free"
                  underlined="hover"
                  key={key}
                  href={href}
                  className="whitespace-nowrap text-md"
                >
                  {name}
                </Link>
              ))}
            </div>
          </div>
        </Container>
      </Skin>
    </footer>
  )
}
